import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import useGa4Event from '@hooks/useGa4Event';
import useHistoryRouter from '@hooks/useLinkTo';
import useModalManager from '@hooks/useModalManager';
import {
  checkAppVersion,
  isNativeAppBoth,
} from '@wconcept/wconcept-common/utils/userAgentUtil';
import {
  addAsIsMWebDomain,
  getMWebTargetUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import useGlobalState from './useGlobalState';

export function useNavigationHandlers() {
  const { appInfo, assistData } = useGlobalState();

  const { sendGa4HeaderClick, sendGa4ActionBarClick } = useGa4Event();
  const { linkTo } = useHistoryRouter();
  const { openLoginConfirm } = useModalManager();
  const pathname = usePathname() ?? '';

  const executeHapticWithLinkTo = useCallback(
    (additionalAction?: () => void) => {
      if (appInfo?.isNativeAppBoth) {
        window.location.href = 'wconceptkr://actionBarHaptic';
      }
      setTimeout(() => {
        additionalAction?.();
      }, 100);
    },
    [appInfo?.isNativeAppBoth]
  );

  const onClickBackBtn = useCallback(() => {
    const isApp =
      typeof navigator !== 'undefined' && isNativeAppBoth(navigator.userAgent);
    if (isApp) {
      window.location.href = 'wconceptkr://webViewBack';
    } else {
      linkTo.back();
    }
  }, [linkTo]);

  const onClickHome = useCallback(() => {
    sendGa4ActionBarClick({ text: 'home' });

    executeHapticWithLinkTo(() => {
      if (appInfo.isNativeAppBoth) {
        window.location.href = 'wconceptkr://actionBarHome';
      } else if (pathname === '/rn/women') {
        window.location.reload();
      } else {
        linkTo.push({
          url: '/rn/women',
        });
      }
    });
  }, [
    appInfo.isNativeAppBoth,
    executeHapticWithLinkTo,
    linkTo,
    pathname,
    sendGa4ActionBarClick,
  ]);

  const onClickSearchBtn = useCallback(async () => {
    sendGa4HeaderClick({ text: 'search' });
    const targetUrl = await getMWebTargetUrl('', '/search/searchInit');
    linkTo.push({ url: targetUrl });
  }, [linkTo, sendGa4HeaderClick]);

  const onClickCartBtn = useCallback(async () => {
    sendGa4HeaderClick({ text: 'cart' });
    const targetUrl = await addAsIsMWebDomain('/Basket');
    linkTo.push({ url: targetUrl });
  }, [linkTo, sendGa4HeaderClick]);

  const onClickCate = useCallback(() => {
    sendGa4ActionBarClick({ text: 'lnb' });

    executeHapticWithLinkTo(() => {
      if (appInfo?.isNativeAppBoth) {
        window.location.href = 'wconceptkr://actionBarLnb';
      } else {
        linkTo.push({
          url: '/rn/lnb',
        });
      }
    });
  }, [
    appInfo?.isNativeAppBoth,
    executeHapticWithLinkTo,
    linkTo,
    sendGa4ActionBarClick,
  ]);

  const onClickSearch = useCallback(async () => {
    sendGa4ActionBarClick({ text: 'search' });

    const targetUrl = await getMWebTargetUrl('', '/search/searchInit');
    executeHapticWithLinkTo(() => {
      linkTo.push({ url: targetUrl });
    });
  }, [executeHapticWithLinkTo, linkTo, sendGa4ActionBarClick]);

  const onClickMyheart = useCallback(async () => {
    sendGa4ActionBarClick({ text: 'myheart' });

    executeHapticWithLinkTo(() => {
      if (assistData?.isLogin) {
        if (pathname.includes('/rn/myheart')) {
          // window.location.reload();
          window.location.href = '/rn/myheart';
        } else {
          linkTo.push({
            url: '/rn/myheart',
          });
        }
      } else {
        openLoginConfirm(
          `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/myheart`,
          true
        );
      }
    });
  }, [
    assistData?.isLogin,
    executeHapticWithLinkTo,
    linkTo,
    openLoginConfirm,
    pathname,
    sendGa4ActionBarClick,
  ]);

  const onClickMypage = useCallback(async () => {
    sendGa4ActionBarClick({ text: 'mypage' });

    executeHapticWithLinkTo(async () => {
      const targetUrl = await addAsIsMWebDomain('/MyPage');
      linkTo.push({
        url: targetUrl,
      });
    });
  }, [executeHapticWithLinkTo, linkTo, sendGa4ActionBarClick]);

  const onClickHistory = useCallback(async () => {
    if (!assistData?.isLogin) {
      openLoginConfirm(
        `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/history`,
        true
      );
      return;
    }
    linkTo.push({
      url: '/rn/history',
    });
  }, [assistData, linkTo, openLoginConfirm]);

  return {
    onClickHome,
    onClickSearchBtn,
    onClickCartBtn,
    onClickCate,
    onClickMyheart,
    onClickSearch,
    onClickMypage,
    onClickBackBtn,
    onClickHistory,
  };
}
